/**
 * SplashScreen — Bootstrap loading screen controller.
 *
 * The splash HTML is inlined in index.html so it renders before any JavaScript
 * loads (no white flash). This class manages progress updates and the final
 * fade-out / removal from the DOM.
 */

export class SplashScreen {
  private readonly element: HTMLElement | null;
  private readonly progressBar: HTMLElement | null;
  private progress = 0;

  constructor() {
    this.element = document.getElementById('edutool-splash');
    this.progressBar = document.getElementById('splash-progress-bar');
  }

  /**
   * Update the progress bar (0-100).
   */
  setProgress(percent: number): void {
    this.progress = Math.max(0, Math.min(100, percent));
    if (this.progressBar) {
      this.progressBar.style.width = `${this.progress}%`;
    }
  }

  /**
   * Increment progress toward 100% without overshooting.
   */
  incrementProgress(delta: number): void {
    this.setProgress(this.progress + delta);
  }

  /**
   * Update the subtitle text (e.g., "Caricamento modelli 3D...").
   */
  setSubtitle(text: string): void {
    const subtitle = this.element?.querySelector('.splash-subtitle');
    if (subtitle) subtitle.textContent = text;
  }

  /**
   * Update the title (e.g., the localized tool name).
   */
  setTitle(text: string): void {
    const title = this.element?.querySelector('.splash-title');
    if (title) title.textContent = text;
  }

  /**
   * Fade out and remove the splash from the DOM.
   * Waits for the CSS transition to complete before removing.
   */
  async hide(): Promise<void> {
    if (!this.element) return;

    // Ensure progress is at 100% before hiding
    this.setProgress(100);

    // Trigger fade-out transition
    this.element.dataset.fading = 'true';

    // Wait for the transition duration (cinematic = 1200ms)
    const duration = this.getTransitionDuration();
    await new Promise((resolve) => setTimeout(resolve, duration));

    // Remove from DOM
    this.element.remove();
  }

  private getTransitionDuration(): number {
    const cs = getComputedStyle(document.documentElement);
    const value = cs.getPropertyValue('--duration-cinematic').trim();
    // Parse "1200ms" or "1.2s"
    if (value.endsWith('ms')) return parseFloat(value);
    if (value.endsWith('s')) return parseFloat(value) * 1000;
    return 1200;
  }
}
